import { MessageDescriptor, useIntl } from 'react-intl';
import { useMemo, useCallback, useEffect } from 'react';
import { getDay } from 'date-fns';
import { RepeatType, VisitRecurrenceDays } from '../../../types/form-data.types';
import {
  useLocale,
  getDateFnsLocale,
  getLocalizedWeekdayKeys,
  STANDARD_DAY_KEYS,
  getLocalizedDayName,
} from 'shared/hooks/use-locale';
import {
  formatOrdinalEn,
  getMonthlyDayOfWeekDescriptor,
  isMonthlyOnLastDay,
  POSITION_MESSAGE_IDS,
} from '../utils/monthly';

interface UseRepeatSectionProps {
  visitDate?: Date | null;
  repeatType: RepeatType;
  recurrenceDays?: VisitRecurrenceDays;
  setRecurrenceDays: (value?: VisitRecurrenceDays) => void;
  clearRepeatEndDate: () => void;
}

interface UseRepeatSectionReturn {
  repeatOptions: Array<{ value: RepeatType; label: string }>;
  customRepeatDays: Array<{ key: keyof VisitRecurrenceDays; label: string }>;
  handleCustomDayToggle: (dayKey: keyof VisitRecurrenceDays, checked: boolean) => VisitRecurrenceDays;
  handleRepeatChange: (value: RepeatType) => VisitRecurrenceDays | undefined;
}

const areRecurrenceDaysEqual = (left?: VisitRecurrenceDays, right?: VisitRecurrenceDays): boolean => {
  if (!left && !right) {
    return true;
  }
  if (!left || !right) {
    return false;
  }
  return STANDARD_DAY_KEYS.every((dayKey) => Boolean(left[dayKey]) === Boolean(right[dayKey]));
};

export const useRepeatSection = ({
  visitDate,
  repeatType,
  recurrenceDays,
  setRecurrenceDays,
  clearRepeatEndDate,
}: UseRepeatSectionProps): UseRepeatSectionReturn => {
  const { formatMessage } = useIntl();
  const locale = useLocale();
  const dateFnsLocale = useMemo(() => getDateFnsLocale(locale), [locale]);
  const localizedWeekdayKeys = useMemo(() => getLocalizedWeekdayKeys(dateFnsLocale), [dateFnsLocale]);

  const repeatOptions = useMemo(() => {
    const weeklyOnDayLabel = visitDate
      ? (() => {
          const dayKey = STANDARD_DAY_KEYS[getDay(visitDate)];
          return formatMessage({
            id: `createVisitFormV2.whenStep.repeat.weeklyOn.${dayKey}`,
            defaultMessage: `Weekly on ${getLocalizedDayName(dayKey, dateFnsLocale)}`,
          });
        })()
      : formatMessage({ id: 'createVisitFormV2.whenStep.repeat.weeklyOnDay', defaultMessage: 'Weekly' });

    // "Monthly on the 28th" — or "Monthly on the last day" when start date is 30th/31st.
    const monthlyDayOfMonthLabel = visitDate
      ? (() => {
          if (isMonthlyOnLastDay(visitDate)) {
            return formatMessage({
              id: 'createVisitFormV2.whenStep.repeat.monthlyOnLastDay',
              defaultMessage: 'Monthly on the last day of every month',
            });
          }
          return formatMessage(
            {
              id: 'createVisitFormV2.whenStep.repeat.monthlyOnDate',
              defaultMessage: 'Monthly on the {dayOfMonth}',
            },
            { dayOfMonth: formatOrdinalEn(visitDate.getDate()) },
          );
        })()
      : null;

    // "Monthly on the first Thursday" — or "Monthly on the last Thursday" when
    // the start date is the last occurrence of its weekday in the month.
    const monthlyDayOfWeekLabel = visitDate
      ? (() => {
          const { position } = getMonthlyDayOfWeekDescriptor(visitDate);
          const dayKey = STANDARD_DAY_KEYS[getDay(visitDate)];
          const weekdayName = getLocalizedDayName(dayKey, dateFnsLocale);
          if (position === -1) {
            return formatMessage(
              {
                id: 'createVisitFormV2.whenStep.repeat.monthlyOnLastWeekday',
                defaultMessage: 'Monthly on the last {weekday}',
              },
              { weekday: weekdayName },
            );
          }
          const positionLabel = formatMessage({ id: POSITION_MESSAGE_IDS[position] } as MessageDescriptor);
          return formatMessage(
            {
              id: 'createVisitFormV2.whenStep.repeat.monthlyOnWeekday',
              defaultMessage: 'Monthly on the {position} {weekday}',
            },
            { position: positionLabel, weekday: weekdayName },
          );
        })()
      : null;

    const options: Array<{ value: RepeatType; label: string }> = [
      {
        value: RepeatType.NONE,
        label: formatMessage({ id: 'createVisitFormV2.whenStep.repeat.none', defaultMessage: 'Does not repeat' }),
      },
      {
        value: RepeatType.DAILY,
        label: formatMessage({ id: 'createVisitFormV2.whenStep.repeat.daily', defaultMessage: 'Daily' }),
      },
      {
        value: RepeatType.WEEKLY,
        label: weeklyOnDayLabel,
      },
      {
        value: RepeatType.WEEKDAY,
        label: formatMessage({
          id: 'createVisitFormV2.whenStep.repeat.weekday',
          defaultMessage: 'Every weekday (Monday to Friday)',
        }),
      },
    ];

    // Both monthly options need a start date to render their dynamic labels.
    if (monthlyDayOfMonthLabel) {
      options.push({ value: RepeatType.MONTHLY_DAY_OF_MONTH, label: monthlyDayOfMonthLabel });
    }
    if (monthlyDayOfWeekLabel) {
      options.push({ value: RepeatType.MONTHLY_DAY_OF_WEEK, label: monthlyDayOfWeekLabel });
    }

    options.push({
      value: RepeatType.CUSTOM,
      label: formatMessage({ id: 'createVisitFormV2.whenStep.repeat.custom', defaultMessage: 'Custom' }),
    });

    return options;
  }, [visitDate, formatMessage, dateFnsLocale]);

  const customRepeatDays = useMemo(
    () =>
      localizedWeekdayKeys.map((dayKey) => ({
        key: dayKey,
        label: formatMessage({
          id: `createVisitFormV2.whenStep.repeat.days.${dayKey}`,
          defaultMessage: getLocalizedDayName(dayKey, dateFnsLocale),
        }),
      })),
    [localizedWeekdayKeys, formatMessage, dateFnsLocale],
  );

  const handleRepeatChange = useCallback(
    (value: RepeatType) => {
      let newRecurrenceDays: VisitRecurrenceDays | undefined;

      switch (value) {
        case RepeatType.WEEKLY:
          if (visitDate) {
            const dayKey = STANDARD_DAY_KEYS[getDay(visitDate)];
            newRecurrenceDays = { [dayKey]: true };
          }
          break;

        case RepeatType.DAILY:
          newRecurrenceDays = {
            monday: true,
            tuesday: true,
            wednesday: true,
            thursday: true,
            friday: true,
            saturday: true,
            sunday: true,
          };
          break;

        case RepeatType.WEEKDAY:
          newRecurrenceDays = {
            monday: true,
            tuesday: true,
            wednesday: true,
            thursday: true,
            friday: true,
          };
          break;

        case RepeatType.MONTHLY_DAY_OF_MONTH:
        case RepeatType.MONTHLY_DAY_OF_WEEK:
          // Monthly variants don't use recurrence_days — BE infers everything
          // (position / weekday / last-day flag) from the visit's start date.
          newRecurrenceDays = undefined;
          break;

        case RepeatType.NONE:
          newRecurrenceDays = undefined;
          clearRepeatEndDate();
          break;

        case RepeatType.CUSTOM:
          newRecurrenceDays = undefined;
          break;

        default:
          newRecurrenceDays = undefined;
          break;
      }

      setRecurrenceDays(newRecurrenceDays);
      return newRecurrenceDays;
    },
    [visitDate, setRecurrenceDays, clearRepeatEndDate],
  );

  useEffect(() => {
    if (repeatType === RepeatType.WEEKLY && visitDate) {
      const dayKey = STANDARD_DAY_KEYS[getDay(visitDate)];
      const nextDays = { [dayKey]: true } as VisitRecurrenceDays;
      if (!areRecurrenceDaysEqual(recurrenceDays, nextDays)) {
        setRecurrenceDays(nextDays);
      }
    }
  }, [repeatType, visitDate, setRecurrenceDays, recurrenceDays]);

  const handleCustomDayToggle = useCallback(
    (dayKey: keyof VisitRecurrenceDays, checked: boolean) => ({
      ...(recurrenceDays ?? {}),
      [dayKey]: checked,
    }),
    [recurrenceDays],
  );

  return {
    repeatOptions,
    customRepeatDays,
    handleCustomDayToggle,
    handleRepeatChange,
  };
};
